import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import validateResetPassword from 'components/commonLayouts/Validations/passwordValidation/validateResetPassword';
import TextBox from 'components/formInputs/textBox/TextBox';
import AuthContext from 'store/AuthContext';
import { completeAmplifyLogin } from 'utils/completeAmplifyLogin';
import FormHeader from './openKnotsLoginHelpers/FormHeader';
import FormHeaderLabel from './openKnotsLoginHelpers/FormHeaderLabel';
import FormButton from './openKnotsLoginHelpers/FormButton';
import PreviousButton from './openKnotsLoginHelpers/PreviousButton';
import OpenKnotsOtpScreen from './OpenKnotsOtpScreen';

const getMsgIconClass = (type) => {
    if (type === 'error') {
        return 'sf-error error-icon';
    }
    if (type === 'success') {
        return 'sf-check success-icon';
    }
    return 'sf-info info-icon';
};

function OpenKnotsResetPasswordComponent(props) {
    const navigate = useNavigate();
    const ctx = useContext(AuthContext);
    const payload = props.payload || {};
    const baseUrlAuth = process.env.REACT_APP_AUTH_MANAGEMENT;
    const [otp, setOtp] = useState();
    const [otpTypeError, setOtpTypeError] = useState(false);
    const [otpTypeErrorMsg, setOtpTypeErrorMsg] = useState();

    const [password, setPassword] = useState();
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState();

    const [confirmPassword, setConfirmPassword] = useState();
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordMessage] = useState();

    const [showModal, setShowModal] = useState(false);

    const [isOtpValid, setIsOtpValid] = useState(true);

    const [isOtpResent, setIsOtpResent] = useState();

    const [showOtpScreen, setShowOtpScreen] = useState(false);
    const [showLoading, setShowLoading] = useState(false);

    const handleKeyPress = (event) => {
        const keyCode = event.keyCode || event.which;
        const keyValue = event.key;
        const isPaste =
            event.ctrlKey || event.metaKey || keyCode === 86 || keyCode === 17 || keyCode === 13 || keyCode === 9;

        const regex = /[0-9\b\r]/;
        if (!regex.test(keyValue) && !isPaste) {
            event.preventDefault();
            setOtpTypeError(true);
            setOtpTypeErrorMsg('Please enter only numbers');
        } else {
            setOtpTypeError(false);
            setOtpTypeErrorMsg();
        }
    };

    const handleOTP = (e) => {
        setIsOtpResent();
        setIsOtpValid(true);
        setOtp(e.target.value);
    };

    const handlePassword = (e) => {
        setPasswordError(false);
        setPasswordErrorMessage();
        setPassword(e.target.value);
    };

    const handleConfirmPassword = (e) => {
        setConfirmPasswordError(false);
        setConfirmPasswordMessage();
        setConfirmPassword(e.target.value);
    };

    const handleResetPassword = () => {
        const { passwordErr, passwordErrMessage } = validateResetPassword(password);

        if (passwordErr) {
            setPasswordError(true);
            setPasswordErrorMessage(passwordErrMessage);
        } else if (password !== confirmPassword) {
            setConfirmPasswordError(true);
            setConfirmPasswordMessage('Password and confirm password should be same');
        } else {
            setShowLoading(true);
            axios
                .post(
                    `${baseUrlAuth}/resetPassword`,
                    {
                        confirmationCode: otp,
                        password: password,
                        userName: payload.userName,
                    },
                    {
                        headers: {
                            'content-type': 'application/json',
                        },
                    }
                )
                .then((res) => {
                    if (res.status === 200) {
                        setShowModal(true);
                    } else {
                        setIsOtpValid(false);
                    }
                })
                .catch(() => {
                    setIsOtpValid(false);
                })
                .finally(() => {
                    setShowLoading(false);
                });
        }
    };

    const handleResendOtp = () => {
        setIsOtpValid(true);
        axios
            .post(
                `${baseUrlAuth}/sendConfirmationCode`,
                { userName: payload.userName },
                {
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                    },
                }
            )
            .then((res) => {
                if (res.status === 200) {
                    setIsOtpResent(true);
                } else {
                    setIsOtpResent(false);
                }
            })
            .catch(() => {
                setIsOtpResent(false);
            });
    };

    const handleSuccessModal = () => {
        navigate('/openknots/login');
    };

    const handleChangePassword = async () => {
        const { passwordErr, passwordErrMessage } = validateResetPassword(password);
        const amplifyAuth = ctx.amplifyAuth();

        if (passwordErr) {
            setPasswordError(true);
            setPasswordErrorMessage(passwordErrMessage);
            return;
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError(true);
            setConfirmPasswordMessage('Password and confirm password should be same');
            return;
        }

        setShowLoading(true);
        try {
            amplifyAuth.configure({ authenticationFlowType: 'USER_SRP_AUTH' });
            let user = await amplifyAuth.signIn(payload.userName, payload.tempPassword);

            if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                await amplifyAuth.completeNewPassword(user, password);
                await amplifyAuth.signOut();
                amplifyAuth.configure({ authenticationFlowType: 'CUSTOM_AUTH' });
                const user2 = await amplifyAuth.signIn(payload.userName, password);
                if (user2.challengeName === 'CUSTOM_CHALLENGE') {
                    ctx.handleUserObject(user2);
                    setShowOtpScreen(true);
                } else if (user2.signInUserSession) {
                    const result = completeAmplifyLogin(user2, { onLogin: ctx.onLogin, defaultPath: '/dashboard?type=openKnots', });
                    if (!result.ok) {
                        await amplifyAuth.signOut().catch(() => { });
                        setConfirmPasswordError(true);
                        setConfirmPasswordMessage(
                            result.reason === 'missing_user_ulid'
                                ? 'Account is not fully provisioned. Contact support.'
                                : 'Unable to complete sign-in. Please try again.',
                        );
                    }
                }
            }
        } catch (err) {
            console.error('Error during handleChangePassword:', err);
            setConfirmPasswordError(true);
            setConfirmPasswordMessage('Password update failed. Please try again.');
        } finally {
            setShowLoading(false);
        }
    };

    const showReset = () => {
        setShowOtpScreen(false);
    };

    let subheading = payload.type === 'resetPassword' ? `Set a new password for account ‘${payload.userName}’` : ``;

    return (
        <>
            {!showOtpScreen && (
                <div className='opk-login-panel'>
                    <div className='opk-login-card opk-reset-password-card d-flex align-items-center justify-content-center flex-column'>
                        <Link to='/openknots/login'>
                            <PreviousButton previousPage='login' />
                        </Link>
                        <FormHeader />
                        <div className='col-12 col-md-5 position-relative bg-white' id='loginInput'>
                            <div className='d-md-flex justify-content-center align-items-center'>
                                <div className='col-12 col-md-10 text-start w-100 d-flex flex-column align-items-center justify-content-center'>
                                    {showModal && (
                                        <div className='msg-modal mb-4 border-success opk-success-modal'>
                                            <p className='float-end mb-0'>
                                                <span
                                                    className='sf-close pe-2 cursor text-neutral-900'
                                                    onClick={handleSuccessModal}></span>
                                            </p>
                                            <div className='media'>
                                                <span
                                                    className={`msg-icon float-start ms-2 ${getMsgIconClass('success')}`}></span>
                                                <div className='media-body text-neutral-900 ms-3'>
                                                    <p className='mt-0 font-b2 text-start font-medium mb-0 px-4 text-neutral-900'>
                                                        SUCCESS
                                                    </p>
                                                    <p className='text-start font-b3 px-4 text-neutral-900'>
                                                        Password Changed Successfully.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <FormHeaderLabel UpperText='Reset Password' LowerText={subheading} fs='28px' />

                                    <div className='col-lg-10 mt-3 w-100'>
                                        <TextBox
                                            label='New Password'
                                            type='password'
                                            id='password'
                                            placeholder='Enter new password'
                                            onChange={handlePassword}
                                            error={passwordError}
                                            errorMessage={passwordErrorMessage}
                                            name='password'
                                            value={password}
                                        />
                                    </div>
                                    <div className='col-lg-10 mt-3 w-100'>
                                        <TextBox
                                            label='Confirm Password'
                                            type='password'
                                            id='confirmPassword'
                                            placeholder='Re-enter new password'
                                            onChange={handleConfirmPassword}
                                            error={confirmPasswordError}
                                            errorMessage={confirmPasswordErrorMessage}
                                            name='confirmPassword'
                                            value={confirmPassword}
                                        />
                                    </div>
                                    {payload.type === 'resetPassword' && (
                                        <>
                                            <div className='col-lg-10 mt-3 w-100'>
                                                <TextBox
                                                    label='OTP'
                                                    type='text'
                                                    id='otp'
                                                    placeholder='Enter OTP'
                                                    onChange={handleOTP}
                                                    error={otpTypeError}
                                                    errorMessage={otpTypeErrorMsg}
                                                    name='otp'
                                                    value={otp}
                                                    onKeyUp={handleKeyPress}
                                                />
                                            </div>
                                            <div className='col-lg-10 w-100 mt-1'>
                                                <p className='font-b3 text-end'>
                                                    <span className='cursor text-light' onClick={handleResendOtp}>
                                                        Resend OTP
                                                    </span>
                                                </p>
                                            </div>
                                            <div className='col-lg-10 mt-3 w-100'>
                                                <FormButton handleLogin={handleResetPassword} Text='Submit' showLoading={showLoading} />
                                            </div>
                                        </>
                                    )}
                                    {payload.type === 'changePassword' && (
                                        <div className='col-lg-10 mt-3 w-100'>
                                            <FormButton
                                                handleLogin={handleChangePassword}
                                                showLoading={showLoading}
                                                Text='CONFIRM'
                                            />
                                        </div>
                                    )}
                                    {!isOtpValid && (
                                        <div className='col-lg-10 mt-3 w-100' role='alert'>
                                            <div
                                                className=' px-3 py-2 rounded mt-3 font-b2 h-100 err-message-background row'
                                                role='alert'>
                                                <i className='sf-warning-filled text-error-700 me-2 d-flex align-items-center justify-content-center col-1'></i>
                                                <span className='text-black font-b2 col-10'>
                                                    Invalid OTP or Attempt limit exceeded.
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {isOtpResent && (
                                        <div
                                            className='col-lg-10 alert alert-success border border-success rounded custom-error-box mt-3 w-100'
                                            role='alert'>
                                            <i className='bi bi-check-circle-fill text-primary-700 me-2'></i>
                                            <span className='text-black fw-600'>OTP sent Successfully</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showOtpScreen && (
                <div className='opk-login-panel'>
                    <div className='opk-login-card'>
                        <PreviousButton previousPage='ResetPassword' showReset={showReset} />
                        <FormHeader />
                        <OpenKnotsOtpScreen
                            userObject={ctx.userObject ? ctx.userObject : payload.userObject}
                            email={payload.userName}
                            password={password}
                            previousPage='ResetPassword'
                            showLogin={showReset}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default OpenKnotsResetPasswordComponent;
